import { Sparkles } from 'lucide-react'

// Re-export the link-popover UIs so existing imports keep resolving.
export { LinkChip, EyebrowAdd } from './valueDesignLinkUI'
export type { LinkChipOption } from './valueDesignLinkUI'

// ===== Severity & importance markers =====================================

const SEVERITY_BG: Record<string, string> = {
  low:    'var(--color-warm-400)',
  medium: 'var(--color-warning-fg)',
  high:   'var(--color-danger-fg)',
}
const SEVERITY_LABEL: Record<string, string> = { low: 'Low', medium: 'Medium', high: 'High' }
const IMPORTANCE_LABEL: Record<string, string> = { nice_to_have: 'Nice', expected: 'Expected', must_have: 'Must' }

export function SeverityDot({ severity }: { severity?: string }) {
  const s = severity || 'medium'
  return (
    <span
      aria-label={`${SEVERITY_LABEL[s] ?? s} severity`}
      className="w-2 h-2 rounded-full justify-self-center"
      style={{ background: SEVERITY_BG[s] ?? SEVERITY_BG.low }}
    />
  )
}
export function severityLabel(severity?: string): string {
  return SEVERITY_LABEL[severity || 'medium'] || severity || ''
}

export function ImportanceRing({ importance }: { importance?: string }) {
  const i = importance || 'expected'
  const ringStyle: React.CSSProperties = i === 'must_have'
    ? { background: 'var(--color-text)', borderColor: 'var(--color-text)' }
    : i === 'expected'
      ? { background: 'var(--color-market)', borderColor: 'var(--color-market)' }
      : { background: 'transparent', borderColor: 'var(--color-market)' }
  return (
    <span
      aria-label={`${IMPORTANCE_LABEL[i]} importance`}
      className="w-2.5 h-2.5 rounded-full justify-self-center"
      style={{ borderWidth: '1.5px', borderStyle: 'solid', ...ringStyle }}
    />
  )
}
export function importanceLabel(importance?: string): string {
  return IMPORTANCE_LABEL[importance || 'expected'] || importance || ''
}

export function SolutionMarker() {
  return (
    <span aria-hidden className="w-2 h-2 rounded-full justify-self-center"
          style={{ background: 'var(--color-solution)' }} />
  )
}

// ===== Eyebrow ===========================================================

export function Eyebrow({ label, count, children }: {
  label: string; count?: number; children?: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted self-end min-w-0">
      <span className="whitespace-nowrap">{label}</span>
      {typeof count === 'number' && (
        <span className="font-medium text-text-secondary">{count}</span>
      )}
      <span className="flex-1 h-px bg-border-subtle" />
      {children}
    </div>
  )
}

export function EyebrowAi({ onClick, loading, disabled, title }: {
  onClick: () => void; loading?: boolean; disabled?: boolean; title?: string
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium normal-case tracking-normal text-text-muted hover:bg-warm-100 hover:text-accent-500 transition-colors disabled:opacity-40"
    >
      <Sparkles className="w-2.5 h-2.5" />
      {loading ? '…' : 'AI draft'}
    </button>
  )
}

// ===== Identity row ======================================================

export function IdentityRow({ side, primary, secondary, trailing, initials, photo }: {
  side: 'customer' | 'solution'
  primary: string
  secondary: string
  trailing?: React.ReactNode
  initials?: string
  photo?: string | null
}) {
  const isCustomer = side === 'customer'
  return (
    <div className="flex items-center gap-3 min-w-0">
      {isCustomer && (
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-[14px] font-semibold leading-none border border-border-subtle overflow-hidden flex-shrink-0"
          style={{
            background: photo ? 'var(--color-warm-200)' : 'var(--color-market-100)',
            color: 'var(--color-market-dark)',
          }}
          aria-hidden
        >
          {photo
            ? <img src={photo} alt="" className="w-full h-full object-cover" />
            : (initials || '··').slice(0, 2).toUpperCase()}
        </div>
      )}
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-[14.5px] font-semibold leading-tight text-text truncate">{primary}</span>
        <span className="text-[12px] text-text-secondary truncate mt-px">{secondary}</span>
      </div>
      {trailing && (
        <span className="flex-shrink-0 pl-3 ml-auto border-l border-border-subtle">
          {trailing}
        </span>
      )}
    </div>
  )
}
